import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import {
  useMasterCategoriesQuery,
  useSellableCategoriesQuery,
} from "../api/queries";
import { MainSubCategorySelect } from "./MainSubCategorySelect";

/** ชื่อ field ใน URL filters — map เป็น query `q` ตอนเรียก API */
export const CATALOG_FILTER_Q_FIELD = "catalogQ";
/** ชื่อ field ใน URL filters — map เป็น query `categoryId` ตอนเรียก API */
export const CATALOG_FILTER_CATEGORY_FIELD = "categoryId";
export const CATALOG_FILTER_PRODUCT_TYPE_FIELD = "productType";
export const CATALOG_FILTER_FITMENT_FIELD = "fitment";
export const CATALOG_FILTER_CURRENCY_FIELD = "currency";

const FILTER_ALL = "__all__";

export const CATALOG_PRODUCT_TYPE_LABELS: Record<string, string> = {
  part_new: "ອະໄຫຼ່ແທ້ (Genuine)",
  part_oem: "ອະໄຫຼ່ OEM",
  part_aftermarket: "ອະໄຫຼ່ທົດແທນ (Aftermarket)",
  part_used: "ອະໄຫຼ່ມືສອງ (Used)",
  service: "ບໍລິການ",
};

export function ShopCatalogFilter() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: "/app/shops/$shopId/catalog" });
  const search = (useSearch({
    from: "/app/shops/$shopId/catalog",
  }) ?? {}) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const qFilter = findCondition(filters, CATALOG_FILTER_Q_FIELD);
  const categoryFilter = findCondition(filters, CATALOG_FILTER_CATEGORY_FIELD);
  const productTypeFilter = findCondition(
    filters,
    CATALOG_FILTER_PRODUCT_TYPE_FIELD,
  );
  const categoriesQ = useMasterCategoriesQuery(shopId);
  const sellableCategoriesQ = useSellableCategoriesQuery(shopId);
  const productCountById = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of sellableCategoriesQ.data?.items ?? []) {
      map.set(item.id, item.count ?? 0);
    }
    return map;
  }, [sellableCategoriesQ.data]);

  const [searchValue, setSearchValue] = useState<string>(
    (qFilter?.value as string) ?? "",
  );

  useEffect(() => {
    setSearchValue((qFilter?.value as string) ?? "");
  }, [qFilter?.value]);

  const pushFilters = (nextFilters: FilterConditionDTO[] | undefined) => {
    if (!shopId) return;
    nav({
      to: "/app/shops/$shopId/catalog",
      params: { shopId },
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const debouncedSyncQToUrl = useDebounceCallback((val: string) => {
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    const trimmed = val.trim();
    if (trimmed) {
      nextFilters = upsertCondition(filters, {
        field: CATALOG_FILTER_Q_FIELD,
        op: "contains",
        value: trimmed,
      });
    } else {
      nextFilters = removeConditions(filters, CATALOG_FILTER_Q_FIELD);
    }
    pushFilters(nextFilters);
  }, 400);

  const categoryIdValue =
    categoryFilter?.value != null && String(categoryFilter.value).trim() !== ""
      ? String(categoryFilter.value)
      : "";

  const productTypeValue =
    productTypeFilter?.value != null &&
    String(productTypeFilter.value).trim() !== ""
      ? String(productTypeFilter.value)
      : "";

  const hasFilter = !!qFilter?.value || !!categoryIdValue || !!productTypeValue;

  const applyEqFilter = (field: string, val: string) => {
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, field);
    } else {
      nextFilters = upsertCondition(filters, {
        field,
        op: "eq",
        value: val,
      });
    }
    pushFilters(nextFilters);
  };

  if (!shopId) return null;

  return (
    <div className="mb-2 flex flex-col gap-2 px-2">
      <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-3">
        <Input
          placeholder="ຄົ້ນຫາ: ຊື່, barcode, OEM..."
          value={searchValue}
          onChange={(e) => {
            const v = e.target.value;
            setSearchValue(v);
            debouncedSyncQToUrl(v);
          }}
          className="h-8 w-full"
        />
        <MainSubCategorySelect
          categories={categoriesQ.data?.items ?? []}
          value={categoryIdValue}
          onChange={(val) => applyEqFilter(CATALOG_FILTER_CATEGORY_FIELD, val)}
          isLoading={categoriesQ.isLoading}
          productCountById={productCountById}
        />
        <Select
          value={productTypeValue || FILTER_ALL}
          onValueChange={(val) =>
            applyEqFilter(
              CATALOG_FILTER_PRODUCT_TYPE_FIELD,
              val === FILTER_ALL ? "" : val,
            )
          }
        >
          <SelectTrigger className="h-8 w-full">
            <SelectValue placeholder="ປະເພດ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL}>ປະເພດ — ທັງໝົດ</SelectItem>
            {Object.entries(CATALOG_PRODUCT_TYPE_LABELS).map(
              ([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                to: "/app/shops/$shopId/catalog",
                params: { shopId },
                search: { ...search, filters: undefined, offset: 0 },
              })
            }
            className="w-fit justify-self-start max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
